import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CARD_SECTION_TITLE_CLASS,
  TABLE_HEADER_CELL_CLASS,
} from '@safetyhub/ui';
import {
  isMissingEndpointError,
  storiesApi,
  type StoryCheckRunSummary,
} from '../../lib/stories';
import {
  coverageAbsenceReason,
  ENV_TONE,
  formatRatio,
  formatRelative,
  parseRegistrySummary,
  queueTotalDisagrees,
  runScopeLabel,
} from './storyMonitorView';

/**
 * 최근 판정 실행 + **보장 수단 분포·커버리지 두 비율** — `GET /admin/stories/check-runs`.
 *
 * ## 🔑 이 패널이 존재하는 이유
 * 숙성(`auto`/`manual`/`none`)은 `guard` **소스 스캔** 파생이라 **서버가 모른다.** run이 그 시점
 * 요약을 `registry_summary`에 **기준(시각·`base_sha`·범위)과 함께** 박고, 서버는 검증 없이 투과한다.
 * ⇒ 그래서 이 수들은 **「지금」이 아니라 「그 run 시점」의 것**이고, 기준을 함께 보여주지 않으면
 * 낡은 수가 현재 수로 읽힌다.
 *
 * ## ⚠️ 클라이언트에서 재파생하지 않는다
 * 관측(= 불변식 결과가 있나)으로 `auto`를 흉내내면 `story:check`의 파생과 조용히 어긋나고, 그것이
 * 「검사가 지워졌는데 화면은 `auto`라고 말하는」 가장 위험한 낡음이다. **투과된 값만 그린다.**
 *
 * ## ⚠️ 그래도 **대조는 한다** — 투과받는 것과 믿는 것은 다르다
 * 재파생 금지는 「값을 **만들지** 말라」이고, 받은 값들이 **서로 맞는지 세는 것**은 그 금지에 걸리지
 * 않는다. 분모 칸이 산수(`전체 − 큐 밖`)를 **글로 인쇄하므로** 그 항등식을 `queueTotalDisagrees`로
 * 대조한다 — 서버는 검증 없이 투과하니 **응답 안의 세 수를 보는 이 자리가 유일한 탐지기**다
 * (2026-10-13 신설. 형제: 위반 네 칸의 `violationTallyDisagrees`).
 *
 * ## ⚠️ 「없다」가 네 가지다 — 한 문구로 뭉치지 않는다
 * ① 엔드포인트가 아직 없다(404) ② 저장된 run이 0건이다 ③ 그 run이 요약을 싣지 않았다
 * ④ 요약 형상이 낯설다. 뭉치면 「엔드포인트가 없다」가 「아무도 안 쟀다」를 덮어 원인이 숨는다.
 * 그래서 호출은 **엔드포인트가 들어오기 전에도 그대로** 하고(404만 갈라 degrade) 들어오면 **한 줄도
 * 바뀌지 않고 켜진다.**
 */
export interface RecentRunsPanelProps {
  /** 목록 필터와 같은 env — 생략(`''`)이면 전 env */
  env: string;
}

const RUN_LIMIT = 10;

function Metric({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: string;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-semibold text-ink-500">{label}</span>
      <span className={`text-xl font-extrabold tabular-nums ${tone ?? 'text-ink-900'}`}>
        {value}
      </span>
      {hint && <span className="text-[11px] text-ink-400">{hint}</span>}
    </div>
  );
}

/** run의 기준 한 줄 — 「어느 시점·어느 코드 형상·어느 범위의 수인가」. */
function RunBasis({ run }: { run: StoryCheckRunSummary }) {
  return (
    <span className="text-[11.5px] leading-relaxed text-ink-500">
      <span className="inline-flex align-middle">
        <Badge tone={ENV_TONE[run.env] ?? 'slate'} size="sm">
          {run.env}
        </Badge>
      </span>{' '}
      {run.trigger} · 측정 {formatRelative(run.measuredAt)} · 기록 {formatRelative(run.judgedAt)} ·{' '}
      {run.baseSha ? (
        <code>
          {run.baseSha.slice(0, 9)}
          {run.baseShaDirty ? ' (dirty)' : ''}
        </code>
      ) : (
        <span className="text-ink-400">기준 sha 없음</span>
      )}{' '}
      ·{' '}
      <strong className={run.specFilter.length === 0 ? '' : 'text-amber-700'}>
        {runScopeLabel(run.specFilter)}
      </strong>
      {run.specFilter.length > 0 && ` — ${run.specFilter.join(', ')}`}
    </span>
  );
}

/** ⚫ 분포를 그릴 수 없을 때 — 사유를 **그 자리에** 적는다(비었음을 드러내는 것이 이 화면의 규율이다). */
function Unavailable({ title, body }: { title: string; body: React.ReactNode }) {
  return (
    <div className="mt-3 border-l-4 border-ink-300 bg-surface-soft px-3 py-2 text-xs leading-relaxed text-ink-600">
      <strong className="text-ink-800">⚫ {title}</strong>
      <div className="mt-1">{body}</div>
      <div className="mt-1">
        지금 그 수를 보려면 <code>pnpm story:check --json</code>을 돌릴 것 — 숙성 파생의 정본은{' '}
        <code>scripts/stories/check.mjs</code>의 <code>maturityOf</code>다.
      </div>
    </div>
  );
}

export default function RecentRunsPanel({ env }: RecentRunsPanelProps) {
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'stories', 'check-runs', { env, limit: RUN_LIMIT }] as const,
    queryFn: () => storiesApi.listCheckRuns({ limit: RUN_LIMIT, ...(env !== '' && { env }) }),
    // ⚠️ 재시도하지 않는다 — 엔드포인트가 아직 없는 서버에서 404를 세 번 치는 것은 낭비이고,
    //    「없다」는 사실은 재시도로 바뀌지 않는다.
    retry: false,
  });

  /**
   * ⚠️ 계약 정렬이 **`judgedAt` 내림차순**이라 이것은 「**기록**이 최신인 run」이고 「측정이 최신인
   * run」이 아니다 — 묵은 결과를 늦게 올리면 그 run이 맨 위에 온다. 그래서 아래 기준 줄이
   * **측정 시각과 기록 시각을 둘 다** 싣는다(둘이 벌어진 것을 거기서 읽는다).
   */
  const latest = data?.items[0];
  const summary = latest ? parseRegistrySummary(latest.registrySummary) : null;

  return (
    <Card>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className={CARD_SECTION_TITLE_CLASS}>보장 수단 · 최근 실행</h2>
        <span className="text-[11px] text-ink-500">
          이 수는 <strong>그 run 시점</strong>의 레지스트리 요약이다 — 「지금」이 아니다
        </span>
      </div>

      {isLoading && <p className="mt-3 text-sm text-ink-500">불러오는 중…</p>}

      {/* ① 엔드포인트가 아직 없다 — 에러가 아니라 사실이다 */}
      {isError && isMissingEndpointError(error) && (
        <Unavailable
          title="이 서버에는 최근 실행 조회가 아직 없다"
          body={
            <>
              <code>GET /admin/stories/check-runs</code>가 404다 — 그 엔드포인트가 아직 배포되지
              않았다(권한 문제가 아니다). 배포되면 이 패널이 <strong>그대로 켜진다</strong>.
            </>
          }
        />
      )}

      {/* ③ 그 외 실패 — 404와 섞지 않는다(403·401·네트워크는 다른 사실이다) */}
      {isError && !isMissingEndpointError(error) && (
        <div className="mt-3 flex items-center gap-3">
          <p className="text-sm text-rose-600">
            최근 실행을 불러올 수 없다
            {error instanceof Error ? ` — ${error.message}` : ''}
          </p>
          <Button variant="secondary" size="sm" onClick={() => void refetch()} disabled={isFetching}>
            다시 시도
          </Button>
        </div>
      )}

      {/* ② 저장된 run이 0건 — 「엔드포인트가 없다」와 다른 사실이다 */}
      {data && data.items.length === 0 && (
        <Unavailable
          title="기록된 판정 실행이 없다"
          body={
            <>
              엔드포인트는 응답하는데 저장된 run이 <strong>0건</strong>이다 — 아무도 아직 판정
              산출물을 올리지 않았다. 위 「실행 방법」의 다섯 단계를 거쳐{' '}
              <code>POST check-runs</code>까지 가면 여기 쌓인다.
            </>
          }
        />
      )}

      {latest && summary === null && (
        <Unavailable
          title={
            latest.registrySummary === null || latest.registrySummary === undefined
              ? '최근 run이 레지스트리 요약을 싣지 않았다'
              : '레지스트리 요약의 형상이 낯설다'
          }
          body={
            latest.registrySummary === null || latest.registrySummary === undefined ? (
              <>
                그 run의 <code>registrySummary</code>가 비어 있다 — 판정은 저장됐지만 숙성 분포는 함께
                올라오지 않았다(러너가 <code>story:check --json</code>의 <code>summary</code>를 싣는다).
              </>
            ) : (
              <>
                <code>total</code>·<code>queueTotal</code>·<code>maturity</code> 네 수 중 읽을 수 없는
                칸이 있다 — 러너 형상이 바뀠다는 신호다. <strong>0으로 메우지 않는다</strong>(메우면
                아무것도 안 재는 것이 완벽으로 보인다).
              </>
            )
          }
        />
      )}

      {latest && summary && (
        <>
          <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Metric
              label="● auto"
              value={String(summary.maturity.auto)}
              hint="사람 없이 재진다"
              tone="text-emerald-600"
            />
            <Metric
              label="◐ manual"
              value={String(summary.maturity.manual)}
              hint="판정 절차가 적혀 있다"
              tone="text-amber-600"
            />
            <Metric
              label="⚫ none"
              value={String(summary.maturity.none)}
              hint="아무것도 없다 ← 작업 목록"
              tone={summary.maturity.none > 0 ? 'text-rose-600' : 'text-ink-900'}
            />
            <Metric
              label="큐 밖 (offQueue)"
              value={String(summary.maturity.offQueue)}
              hint="잴 기대가 없다 — 분모 밖"
              tone="text-ink-600"
            />
          </div>

          <div className="mt-3 flex flex-wrap items-start gap-x-8 gap-y-3 border-t border-line pt-3">
            <Metric
              label="커버리지 (auto+manual)"
              value={formatRatio(summary.coverage)}
              hint="보장 수단이 있다"
            />
            <Metric
              label="자동 커버리지 (auto만)"
              value={formatRatio(summary.autoCoverage)}
              hint="← 숙성도 · 초록의 신뢰도 분모"
            />
            <Metric
              label="분모 (큐)"
              value={`${summary.queueTotal}`}
              hint={`전체 ${summary.total} − 큐 밖 ${summary.maturity.offQueue}`}
              tone={queueTotalDisagrees(summary) ? 'text-rose-600' : undefined}
            />
          </div>

          {/* ⚠️ 위 hint가 **산수를 인쇄한다** — 그러면 그 항등식을 대조해야 한다. 형상 소유자는
              러너고 서버는 투과라(검증 0) 응답 안의 세 수를 여기서 대조하는 것이 **유일한 탐지기**다.
              형제는 위반 네 칸의 `violationTallyDisagrees`이고 같은 이유로 존재한다. */}
          {queueTotalDisagrees(summary) && (
            <p className="mt-2 rounded-card-sm bg-rose-50 px-3 py-2 text-[11.5px] font-semibold text-rose-700">
              ⚠️ 분모의 산수가 어긋난다 — 큐 분모 {summary.queueTotal} ≠ 전체 {summary.total} − 큐 밖{' '}
              {summary.maturity.offQueue} (= {summary.total - summary.maturity.offQueue}). 러너가 낸 세
              수가 서로 맞지 않으니 <strong>위 두 비율을 근거로 쓰지 말 것</strong> — 커버리지 분모가
              그 값이다(러너 <code>summarize</code> 쪽을 봐야 한다).
            </p>
          )}

          {coverageAbsenceReason(summary) === 'queue-empty' && (
            <p className="mt-2 text-[11.5px] font-semibold text-ink-600">
              큐 분모가 0이라 두 비율이 <strong>성립하지 않는다</strong> — 0%도 100%도 아니다.
            </p>
          )}
          {coverageAbsenceReason(summary) === 'not-reported' && (
            <p className="mt-2 text-[11.5px] font-semibold text-amber-700">
              ⚠️ 분모({summary.queueTotal})는 있는데 러너가 비율 칸을 싣지 않았다 — 형상이 바뀠다는
              신호다. 비율을 여기서 계산해 메우지 않는다(그 수의 소유자는 러너다).
            </p>
          )}

          {summary.offQueueDeclared !== null && (
            <p className="mt-2 text-[11.5px] leading-relaxed text-ink-500">
              큐 밖 <strong>선언 전체 {summary.offQueueDeclared}건</strong>
              {summary.offQueueByKind &&
                Object.keys(summary.offQueueByKind).length > 0 &&
                ` (${Object.entries(summary.offQueueByKind)
                  .map(([kind, count]) => `${kind} ${count}`)
                  .join(' · ')})`}
              {summary.offQueueStillMeasured !== null && (
                <>
                  {' — '}그중 <strong>{summary.offQueueStillMeasured}건은 보장 수단이 있어
                  auto·manual로 집계된다</strong>(재판정은 계속된다). 위 「큐 밖{' '}
                  {summary.maturity.offQueue}」과 <strong>다른 수</strong>다.
                </>
              )}
            </p>
          )}

          <div className="mt-3 border-t border-line pt-3">
            <span className="text-[11px] font-bold uppercase tracking-wide text-ink-500">
              이 수의 기준 — 기록이 가장 최신인 run (측정 순서가 아니다)
            </span>
            <div className="mt-1">
              <RunBasis run={latest} />
            </div>
          </div>
        </>
      )}

      {data && data.items.length > 0 && (
        <div className="mt-4 overflow-x-auto border-t border-line pt-3">
          <span className="text-[11px] font-bold uppercase tracking-wide text-ink-500">
            최근 실행 {data.items.length}건 / 전체 {data.total}건
          </span>
          <table className="mt-2 min-w-full text-[12px]">
            <thead>
              <tr>
                <th className={TABLE_HEADER_CELL_CLASS}>측정</th>
                <th className={TABLE_HEADER_CELL_CLASS}>환경 · 트리거</th>
                <th className={TABLE_HEADER_CELL_CLASS}>범위</th>
                <th className={TABLE_HEADER_CELL_CLASS}>판정 (통과/위반/공허/실패)</th>
                <th className={TABLE_HEADER_CELL_CLASS}>기준 sha</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((run) => (
                <tr key={run.id} className="border-b border-line">
                  <td className="px-4 py-2 text-ink-700">{formatRelative(run.measuredAt)}</td>
                  <td className="px-4 py-2">
                    <span className="inline-flex align-middle">
                      <Badge tone={ENV_TONE[run.env] ?? 'slate'} size="sm">
                        {run.env}
                      </Badge>
                    </span>{' '}
                    <span className="text-ink-600">{run.trigger}</span>
                  </td>
                  <td
                    className={`px-4 py-2 ${
                      run.specFilter.length === 0 ? 'text-ink-600' : 'font-semibold text-amber-700'
                    }`}
                    title={run.specFilter.join(', ')}
                  >
                    {runScopeLabel(run.specFilter)}
                  </td>
                  <td className="px-4 py-2 tabular-nums text-ink-700">
                    <span className="text-emerald-700">{run.tally.pass}</span> /{' '}
                    <span className={run.tally.violation > 0 ? 'font-bold text-rose-600' : ''}>
                      {run.tally.violation}
                    </span>{' '}
                    / <span className="text-amber-700">{run.tally.vacuous}</span> /{' '}
                    <span className="text-orange-700">{run.tally.error}</span>
                  </td>
                  <td className="px-4 py-2 text-ink-600">
                    {run.baseSha ? (
                      <code>
                        {run.baseSha.slice(0, 9)}
                        {run.baseShaDirty ? ' (dirty)' : ''}
                      </code>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-[11px] leading-relaxed text-ink-400">
            ⚠️ <strong>범위가 「부분 run」이면 그 판정 수는 전수가 아니다</strong> — 그 칸이 없으면
            부분 run의 「위반 0」이 전수로 읽힌다.
          </p>
        </div>
      )}
    </Card>
  );
}
